import { createServerFn } from "@tanstack/solid-start";
import { withSentrySpan } from "~/lib/sentry-span";
import { authFunctionMiddleware } from "~/server/middleware/auth";
import {
  BankConnectionActionInputSchema,
  BankingConnectionsInputSchema,
  CreateLinkTokenInputSchema,
  ExchangePublicTokenInputSchema,
  UnlinkBankConnectionInputSchema,
} from "./banking.schema";

// ── Rate limiting ─────────────────────────────────────────────────────────────
// Simple sliding-window rate limit per user. Prevents DOS via rapid API calls.

const RATE_LIMIT_WINDOW_MS = 60_000; // 1 minute window
const MAX_LINK_TOKEN_REQUESTS = 10; // Max 10 requests per minute per user
const MAX_EXCHANGE_TOKEN_REQUESTS = 5; // Max 5 requests per minute per user

const linkTokenRequestLog = new Map<string, number[]>();
const exchangeTokenRequestLog = new Map<string, number[]>();

function checkRateLimit(
  userId: string,
  requestLog: Map<string, number[]>,
  maxRequests: number,
): boolean {
  const now = Date.now();
  const userRequests = requestLog.get(userId) ?? [];

  // Remove timestamps outside the window
  const recentRequests = userRequests.filter(
    (ts) => now - ts < RATE_LIMIT_WINDOW_MS,
  );

  if (recentRequests.length >= maxRequests) {
    return false; // Rate limit exceeded
  }

  // Record this request
  recentRequests.push(now);
  requestLog.set(userId, recentRequests);
  return true;
}

// ── Link flow ─────────────────────────────────────────────────────────────────

export const createLinkToken = createServerFn({ method: "POST" })
  .middleware([authFunctionMiddleware])
  .validator((i: unknown) => CreateLinkTokenInputSchema.parse(i))
  .handler(async ({ context, data }) => {
    // Rate limit check
    if (
      !checkRateLimit(
        context.user.id,
        linkTokenRequestLog,
        MAX_LINK_TOKEN_REQUESTS,
      )
    ) {
      throw Response.json(
        {
          error:
            "Too many link token requests. Please wait before trying again.",
        },
        { status: 429 },
      );
    }

    return withSentrySpan("banking.linkToken", async () => {
      const { createBankingLinkTokenForUser } =
        await import("./banking.server");
      return createBankingLinkTokenForUser(context.user.id, data.businessId);
    });
  });

export const exchangePublicToken = createServerFn({ method: "POST" })
  .middleware([authFunctionMiddleware])
  .validator((i: unknown) => ExchangePublicTokenInputSchema.parse(i))
  .handler(async ({ context, data }) => {
    // Rate limit check
    if (
      !checkRateLimit(
        context.user.id,
        exchangeTokenRequestLog,
        MAX_EXCHANGE_TOKEN_REQUESTS,
      )
    ) {
      throw Response.json(
        {
          error: "Too many exchange requests. Please wait before trying again.",
        },
        { status: 429 },
      );
    }

    return withSentrySpan("banking.exchangeToken", async () => {
      const { exchangeBankingPublicTokenForUser } =
        await import("./banking.server");
      return exchangeBankingPublicTokenForUser(
        context.user.id,
        data.businessId,
        data.publicToken,
        data.institution,
        data.connectionId,
      );
    });
  });

// ── Connection management ────────────────────────────────────────────────────

export const getBankingConnections = createServerFn({ method: "GET" })
  .middleware([authFunctionMiddleware])
  .validator((i: unknown) => BankingConnectionsInputSchema.parse(i))
  .handler(async ({ context, data }) => {
    return withSentrySpan("banking.connections", async () => {
      const { getBankingOverviewForUser } = await import("./banking.server");
      return getBankingOverviewForUser(context.user.id, data.businessId);
    });
  });

export const syncBankingConnection = createServerFn({ method: "POST" })
  .middleware([authFunctionMiddleware])
  .validator((i: unknown) => BankConnectionActionInputSchema.parse(i))
  .handler(async ({ context, data }) => {
    return withSentrySpan("banking.syncNow", async () => {
      const { syncBankingConnectionNowForUser } =
        await import("./banking.server");
      return syncBankingConnectionNowForUser(
        context.user.id,
        data.businessId,
        data.connectionId,
      );
    });
  });

export const reconnectBankingConnection = createServerFn({ method: "POST" })
  .middleware([authFunctionMiddleware])
  .validator((i: unknown) => BankConnectionActionInputSchema.parse(i))
  .handler(async ({ context, data }) => {
    return withSentrySpan("banking.reconnect", async () => {
      const { createBankingReconnectLinkTokenForUser } =
        await import("./banking.server");
      return createBankingReconnectLinkTokenForUser(
        context.user.id,
        data.businessId,
        data.connectionId,
      );
    });
  });

export const unlinkBankingConnection = createServerFn({ method: "POST" })
  .middleware([authFunctionMiddleware])
  .validator((i: unknown) => UnlinkBankConnectionInputSchema.parse(i))
  .handler(async ({ context, data }) => {
    return withSentrySpan("banking.unlink", async () => {
      const { unlinkBankingConnectionForUser } =
        await import("./banking.server");
      return unlinkBankingConnectionForUser(
        context.user.id,
        data.businessId,
        data.connectionId,
        data.mode,
      );
    });
  });
